import React from 'react';
import { Package, Star } from 'lucide-react';
import { Input } from '../ui/input';
import { Badge } from '../ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { UNIT_OPTIONS, catCfg, catLabel } from './categoryConfig';
import { TileGrid, BackButton, SectionHeader } from '../common/TileGrid';

// Grille de grandes tuiles par catégorie → liste des produits d'une catégorie (ou résultats de recherche)
export const CategoryTiles = ({ categories, products, quantities, onSelect, favorites = {} }) => (
  <TileGrid onSelect={onSelect} tiles={categories.map(cat => {
    const catProducts = products.filter(p => p.category === cat);
    const filled = catProducts.filter(p => quantities[p.id] > 0).length;
    const favs = catProducts.filter(p => favorites[p.id]).sort((a, b) => favorites[b.id] - favorites[a.id]).slice(0, 3);
    const cfg = catCfg(cat);
    const sub = favs.length
      ? <span className="flex items-start gap-1 text-gray-500" data-testid={`cat-tile-favs-${cat}`}><Star size={11} className="mt-0.5 flex-shrink-0 text-amber-500 fill-amber-400" /><span className="line-clamp-2">{favs.map(p => shortName(p.name)).join(' · ')}</span></span>
      : `${catProducts.length} produits`;
    return { id: cat, label: catLabel(cat), sub, icon: cfg.icon, cfg, badge: filled };
  })} />
);

// « BOF/ Beurre - Butter » → « Beurre »
export const shortName = (name) => (name || '').replace(/^[A-Z&]{2,6}\s*\/\s*/i, '').split(' - ')[0].trim();

export const ProductGroups = ({ grouped, quantities, units, onQty, onUnit, onBack, readOnly, favorites = {} }) => (
  <div className="space-y-4 mb-8">
    {onBack && <BackButton onClick={onBack} />}
    {Object.keys(grouped).length === 0 && <div className="border border-dashed border-gray-300 rounded-2xl p-8 text-center text-gray-500" data-testid="products-empty">Aucun produit trouvé.</div>}
    {Object.entries(grouped).map(([category, list]) => {
      const cfg = catCfg(category);
      const catProducts = [...list].sort((a, b) => (favorites[b.id] || 0) - (favorites[a.id] || 0));
      const favCount = catProducts.filter(p => favorites[p.id]).length;
      const filled = catProducts.filter(p => quantities[p.id] > 0).length;
      return (
        <div key={category} className={`bg-white border-2 ${cfg.border} rounded-3xl overflow-hidden`} data-testid={`cat-section-${category}`}>
          <SectionHeader icon={cfg.icon || Package} cfg={cfg} label={catLabel(category)} sub={`${catProducts.length} produits${favCount ? ` · ${favCount} habitué${favCount > 1 ? 's' : ''} en tête` : ''}`} right={filled > 0 && <Badge className="bg-neon-pink text-white border-0">{filled}</Badge>} />
          <div className="divide-y divide-gray-100">
            {catProducts.map((product, idx) => {
              const hasQty = quantities[product.id] > 0;
              const isFav = !!favorites[product.id];
              const lastFav = isFav && !favorites[catProducts[idx + 1]?.id];
              return (
                <div key={product.id} className={`px-3 md:px-5 py-2.5 flex items-center gap-2 ${hasQty ? 'bg-neon-pink/5' : isFav ? 'bg-amber-50/40 hover:bg-amber-50' : 'hover:bg-gray-50'} ${lastFav ? 'border-b-2 border-b-amber-200' : ''}`} data-testid={`product-row-${product.id}`}>
                  {isFav && <Star size={14} className="text-amber-500 fill-amber-400 flex-shrink-0" data-testid={`fav-star-${product.id}`} />}
                  <span className={`font-medium text-sm flex-1 truncate ${hasQty ? 'text-gray-900' : 'text-gray-700'}`}>{product.name}</span>
                  <Input type="number" inputMode="decimal" step="any" min="0" value={quantities[product.id] ?? ''} onChange={(e) => onQty(product.id, e.target.value)} placeholder="Qte" disabled={readOnly}
                    className={`h-9 w-16 px-2 rounded-lg text-center text-sm font-medium flex-shrink-0 ${hasQty ? 'bg-neon-pink/10 border-neon-pink/30 text-neon-pink' : 'bg-gray-100 border-gray-200 text-gray-900'}`} data-testid={`qty-${product.id}`} />
                  <Select value={units[product.id] || product.unit || ''} onValueChange={(v) => onUnit(product.id, v)} disabled={readOnly}>
                    <SelectTrigger className="h-9 w-[4.5rem] rounded-lg bg-gray-100 border-gray-200 text-gray-700 text-xs flex-shrink-0"><SelectValue placeholder="Unité" /></SelectTrigger>
                    <SelectContent className="bg-white border-gray-200">{[...new Set([...UNIT_OPTIONS, product.unit].filter(Boolean))].map(u => <SelectItem key={u} value={u}>{u}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
              );
            })}
          </div>
        </div>
      );
    })}
  </div>
);
